import { defineStore } from 'pinia';
import { ref, toRaw } from 'vue';
import type { TableArticleDto } from '@/ApiClient';

import { CACHE_TTL, onTabResume } from '@/store/cache-ttl';
import { entityCache, keyedLruCache } from '@/store/cache-primitives';
import { signalrListen } from '@lib/signalr';

export interface TableViewCache {
  filterKey: string;
  articleIds: (number | null)[]; // null = skeleton
  focusedReference: { primary?: string | number; articleNumber?: string; index?: number } | null;
}

// TableViewCache is a frontend-defined shape, so the DTO schema hash alone can't guard
// it — bump this whenever the snapshot/TableViewCache shape changes.
const SNAPSHOT_VERSION = 2;

// View caches are keyed per filter combination so switching filters back and forth
// restores instantly (each with its own scroll position).
const MAX_VIEW_CACHES = 20;

type ArticleTableSnapshot = {
  version: number;
  articles: Record<number, TableArticleDto>;
  viewCaches: Record<string, TableViewCache>;
};

export const useArticleTable = defineStore('article-table', () => {
  const entities = entityCache<TableArticleDto>();
  const articles = entities.data;
  const revision = ref(0);
  const lru = keyedLruCache<TableViewCache>(MAX_VIEW_CACHES);
  const viewCaches = lru.data;

  function setViewCache(cache: TableViewCache) {
    lru.set(cache.filterKey, cache);
  }

  // Snapshot persistence contract — see store/warm-boot.ts. Restores from disk-seeded
  // view caches always revalidate (every restore does — see fetchInitialArticles in
  // pages/products/table-view/table-store.ts), so the seeded data being old is fine.
  const snapshotDto = 'TableArticleDto' as const;

  function snapshotData(): ArticleTableSnapshot {
    // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
    return { version: SNAPSHOT_VERSION, articles: toRaw(articles.value), viewCaches: toRaw(viewCaches.value) };
  }

  function seedSnapshot(data: unknown) {
    const snapshot = data as Partial<ArticleTableSnapshot>;
    if (snapshot.version !== SNAPSHOT_VERSION) return;
    if (Object.keys(articles.value).length > 0 || Object.keys(viewCaches.value).length > 0) return;
    entities.seed(snapshot.articles ?? {});
    viewCaches.value = snapshot.viewCaches ?? {};
  }

  function update(article: TableArticleDto) {
    entities.set(article);
    revision.value++;
  }

  function remove(articleId: number) {
    entities.remove([articleId]);
    for (const cache of Object.values(viewCaches.value)) {
      cache.articleIds = cache.articleIds.filter((id) => id !== articleId);
    }
    revision.value++;
  }

  function clear() {
    entities.clear();
    lru.clear();
  }

  onTabResume((elapsed) => {
    if (elapsed >= CACHE_TTL) {
      // Drop only the list/scroll caches so the next table mount fetches a fresh list;
      // keep the article DTOs so anything rendered shows data instead of skeletons.
      lru.clear();
    }
  });

  // Timeline health is derived from the plan's anchor date and gates, not from anything on the
  // article row, so a timeline edit can change every row without any article being saved.
  // Called by listenAllGlobalStores once SignalR is connected.
  function listen() {
    signalrListen('TimelineUpdate', clear, { listenForever: true });
  }

  return {
    articles,
    revision,
    listen,
    viewCaches,
    setViewCache,
    snapshotDto,
    snapshotData,
    seedSnapshot,
    cache: entities.cache,
    update,
    remove,
    clear,
  };
});
